// The Task picker on a time line offers two kinds of work: the client's
// regular tasks (T1/T2 files, clients.Task) and its Other tasks (PST filing,
// bookkeeping, ... -- clients.OtherTask). They are separate tables with
// separate id sequences, so Task 12 and OtherTask 12 can both exist. The
// picker's option values therefore carry the kind: "t:12" / "o:12". Nothing
// outside this file sees those strings -- the line itself stores `task` and
// `other_task` as plain ids, exactly as the API does.

const DUE_FMT = { month: "short", day: "numeric", year: "numeric" };

// Same label in the grid and the timer, so a task reads identically in both.
export function taskLabel(t) {
  const fy = t.fiscal_year ? ` FY${t.fiscal_year}` : "";
  if (t.name) return `${t.name}${fy}`;
  return `${t.task_category || "Task"}${fy}`;
}

// "PST filing · due Nov 30, 2026 (Other)". The suffix is what tells an Other
// task apart from a regular task that happens to share its name.
export function otherTaskLabel(o) {
  const due = o.due_date
    ? ` · due ${new Date(`${o.due_date}T00:00:00`).toLocaleDateString("en-US", DUE_FMT)}`
    : "";
  return `${o.name || "Other task"}${due} (Other)`;
}

// Open Other tasks only: a completed one refuses time server-side. Planned
// (future-dated) ones stay in -- logging the first hour is how they start.
export function openOtherTasks(list) {
  return (list || []).filter((o) => !o.is_completed);
}

// Regular tasks first, then the client's Other tasks.
export function taskPickerOptions(tasks, otherTasks) {
  return [
    ...(tasks || []).map((t) => ({ value: `t:${t.id}`, label: taskLabel(t) })),
    ...(otherTasks || []).map((o) => ({ value: `o:${o.id}`, label: otherTaskLabel(o) })),
  ];
}

// The picker value for a line (or a timer) that names either kind.
export function pickerValue(item) {
  if (item?.task) return `t:${item.task}`;
  if (item?.other_task) return `o:${item.other_task}`;
  return null;
}

// A picked value back into the two fields it sets. Choosing one kind always
// clears the other: a line names a task OR an Other task, never both.
export function parsePickerValue(v) {
  const s = v == null ? "" : String(v);
  if (s.startsWith("o:")) return { task: null, other_task: Number(s.slice(2)) || null };
  if (s.startsWith("t:")) return { task: Number(s.slice(2)) || null, other_task: null };
  // A bare id is a regular task -- the shape every caller used before Other
  // tasks were pickable.
  return { task: s ? (Number(s) || s) : null, other_task: null };
}

export const asList = (r) => (Array.isArray(r) ? r : (r?.results || []));
